import { ChangeDetectionStrategy, Component, HostListener, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Popover } from 'primeng/popover';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogComponent } from 'src/app/shared/components/competitions/dialogs/confirm-dialog/confirm-dialog.component';
import { Menu } from 'src/app/types';
import { ParentMenu } from 'src/app/types/Menu';
import { UserDetailService } from 'src/app/shared/services/user-detail.service';
import { MenuService } from '../menu.service';
import { NavService } from '../nav.service';

@Component({
  selector: 'app-side-nav-container',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, PopoverModule, TooltipModule, ConfirmDialogComponent],
  templateUrl: './side-nav-container.component.html',
  styleUrls: ['./side-nav-container.component.scss'],
})
export class SideNavContainerComponent implements OnInit, OnDestroy {
  protected readonly router = inject(Router);
  protected readonly userDetailService = inject(UserDetailService);
  protected readonly menuService = inject(MenuService);
  protected readonly navService = inject(NavService);
  protected readonly destroy$ = new Subject<void>();

  readonly submenuPanel = viewChild.required<Popover>('submenuPanel');

  protected readonly parentClubLogo = signal<string>('');
  protected readonly userName = signal<string>('');
  protected readonly userEmail = signal<string>('');
  protected readonly roleType = signal<string>("");
  protected readonly isBookingTabActive = signal(false);
  protected readonly isSuperUserLoggedIn = signal(false);
  protected readonly menus = signal<Menu[]>([]);
  protected readonly selectedMenuIndex = signal(-1);
  protected readonly selectedSubMenuIndex = signal(-1);
  protected readonly selectedParentMenuIndex = signal(-1);
  protected readonly superMenuSelected = signal(false);
  protected readonly collapsed = signal(false);
  protected readonly currentSubmenuItems = signal<any[]>([]);
  protected readonly showLogoutConfirm = signal(false);

  private isTablet = false;

  ngOnInit(): void {
    this.checkScreenSize();

    this.navService.isSideNavCollapsed$.pipe(takeUntil(this.destroy$)).subscribe(isCollapsed => {
      this.collapsed.set(isCollapsed || this.isTablet);
    });

    this.menuService.menus$.pipe(takeUntil(this.destroy$)).subscribe(menus => {
      this.menus.set(menus);
    });

    this.userDetailService.loggedOnUserDetails$.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.parentClubLogo.set(data?.parentClubLogo ?? '');
      this.userName.set(data?.name ?? '');
      this.userEmail.set(data?.emailId ?? '');
      if (data?.roleType) {
        this.menuService.setRoleType(data.roleType, data?.isAllowForBooking ?? false);
      }
    });

    this.userDetailService.isSuperUserLoggedIn.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.isSuperUserLoggedIn.set(res);
    });

    this.router.events.pipe(
      filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      this.setActiveMenuFromRoute(event.url);
    });

    this.setActiveMenuFromRoute(this.router.url);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  protected setActiveMenuFromRoute(url: string): void {
    this.selectedMenuIndex.set(-1);
    this.selectedSubMenuIndex.set(-1);
    this.selectedParentMenuIndex.set(-1);
    this.superMenuSelected.set(false);

    if (url.includes('superUserDashboardComponent')) {
      this.superMenuSelected.set(true);
      return;
    }

    this.menus().forEach((menu, menuIndex) => {
      if (menu.route && url.includes(menu.route) && menu.subMenu.length === 0) {
        this.selectedMenuIndex.set(menuIndex);
        return;
      }
      if (menu.subMenu?.length > 0) {
        menu.subMenu.forEach((subMenu, subMenuIndex) => {
          if (subMenu.route && url.includes(subMenu.route)) {
            this.selectedMenuIndex.set(menuIndex);
            this.selectedSubMenuIndex.set(subMenuIndex);
            this.selectedParentMenuIndex.set(menuIndex);
            menu.isClicked = true;
          }
        });
      }
    });
  }

  protected onSuperUserClick(): void {
    this.router.navigate(['main/dashboard/superUserDashboardComponent']);
    this.selectSuper();
  }

  protected logout(): void {
    this.showLogoutConfirm.set(true);
  }

  protected performLogout(): void {
    this.showLogoutConfirm.set(false);
    localStorage.clear();
    this.router.navigateByUrl('/auth/login');
  }

  protected maskedEmail(): string {
    const email = this.userEmail();
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const visible = local.slice(0, 3);
    return `${visible}***@${domain}`;
  }

  protected selectSuper(): void {
    this.superMenuSelected.set(true);
    this.selectedMenuIndex.set(-1);
    this.selectedSubMenuIndex.set(-1);
    this.selectedParentMenuIndex.set(-1);
  }

  protected menuSelected(menu: Menu, i: number): void {
    if (menu.subMenu.length === 0) {
      this.selectedMenuIndex.set(i);
      this.selectedSubMenuIndex.set(-1);
      this.selectedParentMenuIndex.set(-1);
      this.superMenuSelected.set(false);
    }
  }

  protected subMenuRoute(subMenu: ParentMenu, i: number): void {
    this.router.navigate([subMenu.route]);
    this.selectedSubMenuIndex.set(i);
    this.selectedParentMenuIndex.set(this.selectedMenuIndex());
    this.superMenuSelected.set(false);
  }

  protected isParentMenuSelected(index: number): boolean {
    return this.selectedParentMenuIndex() === index && this.selectedSubMenuIndex() !== -1;
  }

  protected toggleSubmenu(menu: Menu, i: number, event: Event): void {
    if (menu.subMenu.length > 0) {
      this.currentSubmenuItems.set(menu.subMenu);
      this.selectedMenuIndex.set(i);
      this.superMenuSelected.set(false);
      this.submenuPanel().toggle(event);
    } else {
      this.router.navigate([menu.route]);
      this.selectedMenuIndex.set(i);
      this.selectedSubMenuIndex.set(-1);
      this.selectedParentMenuIndex.set(-1);
      this.superMenuSelected.set(false);
    }
  }

  protected hasRoute(route: string): boolean {
    return this.router.url.includes(route);
  }

  private checkScreenSize(): void {
    this.isTablet = window.innerWidth <= 600;
    if (this.isTablet) {
      this.collapsed.set(true);
    }
  }
}
